import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { dispatcher } from 'dispatcher';
import { path, pick } from 'ramda';

import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { BalancePrompt } from '@interlayer/js/app/components/common/WidgetAccount/components/BalancePrompt';
import { Balance } from '@interlayer/js/app/components/main/Balance';
import { GamesSearchGlobal } from '@interlayer/js/app/components/main/GameSearchGlobal';
import { InboxCounter } from '@interlayer/js/app/components/main/InboxCounter';
import { QuickDepositIframe } from '@interlayer/js/app/components/main/QuickDepositIframe';
import { Button } from '@interlayer/js/components/common/Button';

import { useTranslations } from '@core/js/hooks/i18n';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';

import { useCloseOtherWidgets } from '@interlayer/js/app/components/common/WidgetAccount/hooks/useCloseOtherWidgets';
import { BALANCE_TYPES } from '@interlayer/js/app/components/main/Balance/constants';
import { useBalanceNameList } from '@interlayer/js/app/components/main/Balance/hooks/useBalanceNameList';
import { useSwitchAccountMenu } from '@interlayer/js/app/components/main/DefaultHeader/components/HeaderMobile/hooks';
import {
    DELAY_GAME_SEARCH_INPUT_IN_FOCUS,
    GAME_SEARCH_HEADER_ID
} from '@interlayer/js/app/components/main/GameSearchGlobal/constants';
import { useCurrentFormFactor, useInDesktop } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import { useDynamicTagList } from '@interlayer/js/app/hooks/useDynamicTagList';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { setUrlRedirectBackAfterDeposit } from '@interlayer/js/components/system/App/store/buzz/actions';
import {
    useUnreadMessagesCount
} from '@interlayer/js/components/system/CommunicationWidgets/buzz/hooks/useUnreadMessagesCount';
import { useOpenInbox } from '@interlayer/js/components/system/CommunicationWidgets/hooks/useOpenInbox';
import { useNavigate } from '@interlayer/js/hooks/navigate';
import { useGetPaymentStatistics } from '@interlayer/js/hooks/user/hooks/useGetPaymentStatistics';

import { WIDGET_NAMES } from './constants';
import { TWidgetAccountProps } from './types';

import style from './WidgetAccount.scss';

export const WidgetAccount: React.FC<TWidgetAccountProps> = ({ setSearchShow, isSearchShow, isCasinoEnv }) => {
    const navigate = useNavigate();
    const isDesktop = useInDesktop();
    const switchAccountMenu = useSwitchAccountMenu();
    const balanceNameList = useBalanceNameList();
    const dispatch = useDispatch();
    const onOpenInbox = useOpenInbox();
    const location = useLocation<{ prevPath?: string }>();
    const currentFormFactor = useCurrentFormFactor();
    const { paymentsStatistics, allPlayerTags: playerTags } = useUserSelector(pick(['paymentsStatistics', 'allPlayerTags']));
    const getPaymentStatistics = useGetPaymentStatistics(false);
    const isShowInbox = useAppSelector(path(['inboxWidget', 'showIcon']));
    const t = useTranslations('playtech.mobile.system.header.');
    const tb = useTranslations('playtech.mobile.system.myAccount.label.');
    const [
        dTagQuickDepositPlatform = '',
        dTagDepositPageUrl = '/deposit',
        dTagHeaderBalanceType = BALANCE_TYPES.casino_gaming_balance
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.quickDepositPlatform,
        DYNAMIC_TAG_NAMES.depositPageUrl,
        DYNAMIC_TAG_NAMES.headerBalanceType
    ]);
    const quickDepositBlockTags = useDynamicTagList({
        tagName: DYNAMIC_TAG_NAMES.quickDepositBlockTags,
        defaultValue: '',
        withLowerCase: false
    }).map((tag: string) => tag.split('='));
    const inboxCount = useUnreadMessagesCount('inbox');
    const quickDepositPlatforms = dTagQuickDepositPlatform
        .toLowerCase()
        .split(',')
        .map((platform) => platform.trim());

    const [isBalanceOpened, setBalanceOpened] = useState(false);
    const [isQuickDepositOpened, setQuickDepositOpened] = useState(false);
    const [isQuickDepositOpenedFirstTime, setQuickDepositOpenedFirstTime] = useState(false);
    const closeWidgets = useCloseOtherWidgets(setBalanceOpened, setQuickDepositOpened);

    useEffect(() => {
        dispatcher.listen('gameSearchInFocus', closeWidgets);

        return () => {
            dispatcher.removeListener('gameSearchInFocus', closeWidgets);
        };
    }, [closeWidgets]);

    const handlerClickAccount = () => {
        closeWidgets();
        switchAccountMenu();
    };

    const handleQuickDepositClick = useCallback(async () => {
        const isRedirectToDepositPage = !isQuickDepositOpened
            && !parseInt(paymentsStatistics.deposits?.totalDepositCount)
            && !parseInt((await getPaymentStatistics())?.deposits?.totalDepositCount)
            || quickDepositBlockTags.length
            && quickDepositBlockTags.some(([name, value]) => playerTags?.[name] && playerTags[name].value === value);

        closeWidgets(WIDGET_NAMES.QUICK_DEPOSIT);

        if (!quickDepositPlatforms.includes(currentFormFactor.toLowerCase()) || isRedirectToDepositPage) {
            dispatch(setUrlRedirectBackAfterDeposit(location.pathname));
            navigate(dTagDepositPageUrl);

            return;
        }

        if (!isQuickDepositOpenedFirstTime) {
            setQuickDepositOpenedFirstTime(true);
        }

        setQuickDepositOpened(!isQuickDepositOpened);
    }, [
        closeWidgets,
        currentFormFactor,
        dTagDepositPageUrl,
        dispatch,
        getPaymentStatistics,
        isQuickDepositOpened,
        isQuickDepositOpenedFirstTime,
        location.pathname,
        navigate,
        paymentsStatistics.deposits?.totalDepositCount,
        playerTags,
        quickDepositBlockTags,
        quickDepositPlatforms
    ]);

    const handleBalanceClick = () => {
        closeWidgets(WIDGET_NAMES.BALANCE);
        setBalanceOpened(!isBalanceOpened);
    };

    const handleInboxClick = () => {
        closeWidgets(WIDGET_NAMES.INBOX);
        onOpenInbox();
    };

    const handleSearchClick = () => {
        closeWidgets();
        setSearchShow(true);

        setTimeout(() => {
            document.getElementById(GAME_SEARCH_HEADER_ID)?.focus();
        }, DELAY_GAME_SEARCH_INPUT_IN_FOCUS);
    };

    useEffect(() => {
        if (location.state?.prevPath !== location.pathname) {
            setBalanceOpened(false);
            setQuickDepositOpened(false);

            if (window.Communication?.API?.inbox?.getWindowStatus()?.open) {
                onOpenInbox();
            }
        }
    }, [location, onOpenInbox]);

    const handleCloseBalanceInfo = () => {
        setBalanceOpened(false);
    };

    return (
        <div
            data-testid="WidgetAccount_wrapper"
            className={clsx(style.widgetWrap, { [style.isInbox]: isShowInbox })}
        >
            <div className={style.widgetBottom}>
                {isDesktop && (
                    <>
                        <div className={style.balanceContainer}>
                            <div
                                onClick={handleBalanceClick}
                                className={style.balanceWrap}
                                data-testid="WidgetAccount_balance"
                            >
                                <Suspense fallback="">
                                    <Balance type={dTagHeaderBalanceType} className={style.balance} />
                                </Suspense>

                                <SvgIcon className={style.arrow} type="arrow-down" />
                            </div>

                            <BalancePrompt isCasinoEnv={isCasinoEnv} />
                        </div>

                        <Button
                            data-testid="WidgetAccount_deposit"
                            size="medium"
                            view={isCasinoEnv ? 'primary' : 'secondary'}
                            type="button"
                            className={style.depositMob}
                            onClick={handleQuickDepositClick}
                        >
                            {t('mobile.quick-deposit')}
                        </Button>
                    </>
                )}

                {isShowInbox && isDesktop && (
                    <div
                        className={style.inbox}
                        onClick={handleInboxClick}
                        data-testid="WidgetAccount_inbox"
                    >
                        <InboxCounter className={style.inboxCounter} type="inbox">
                            <SvgIcon className={style.inboxIcon} type="mail" />
                        </InboxCounter>
                    </div>
                )}

                <div className={style.balanceContainer}>
                    <div
                        className={style.account}
                        onClick={handlerClickAccount}
                        data-testid="WidgetAccount_account"
                    >
                        <div className={style.iconBlock}>
                            <SvgIcon
                                className={style.accountIcon}
                                type="buddy"
                            />

                            {inboxCount > 0 && !isDesktop && (
                                <span className={style.inboxLabel} />
                            )}
                        </div>

                        {!isDesktop && (
                            <Suspense fallback="">
                                <Balance type={dTagHeaderBalanceType} className={style.balance} />
                            </Suspense>
                        )}
                    </div>

                    {!isDesktop && <BalancePrompt isCasinoEnv={isCasinoEnv} />}
                </div>

                {!isDesktop && (
                    <>
                        <Button
                            size="small"
                            view={isCasinoEnv ? 'primary' : 'secondary'}
                            type="button"
                            className={style.depositMob}
                            onClick={handleQuickDepositClick}
                        >
                            {t('mobile.quick-deposit')}
                        </Button>

                        <SvgIcon
                            onClick={handleSearchClick}
                            data-testid="WidgetAccount_search"
                            type="search-thin"
                            className={style.search}
                        />
                    </>
                )}

                {isSearchShow && (
                    <div className={style.searchBlock}>
                        <GamesSearchGlobal
                            className={style.wrap}
                            onCloseSearch={() => setSearchShow(false)}
                        />

                        <SvgIcon
                            type="close-popup"
                            className={style.close}
                            onClick={() => setSearchShow(false)}
                            data-testid="WidgetAccount_closeSearch"
                        />
                    </div>
                )}
            </div>

            {isDesktop && (
                <div
                    data-testid="WidgetAccount_balanceBlock"
                    className={clsx(style.balanceBlock, { [style.opened]: isBalanceOpened })}
                    style={{ maxHeight: `${balanceNameList.length * (isCasinoEnv ? 4.4 : 3.45) + 0.2}rem` }}
                >
                    {isCasinoEnv && (
                        <>
                            <div className={style.balanceTitle}>
                                {t('balance')}
                            </div>

                            <SvgIcon
                                width="1rem"
                                height="1rem"
                                onClick={handleCloseBalanceInfo}
                                className={style.close}
                                type="close-sidebar"
                            />

                            <span className="divider-bg" />
                        </>
                    )}

                    {balanceNameList.map(balanceName => (
                        <div className={style.balanceItem} key={balanceName}>
                            <span className={style.balanceLabel}>{tb(balanceName)}</span>

                            <Suspense fallback="">
                                <Balance type={balanceName} className={style.balanceValue} />
                            </Suspense>
                        </div>
                    ))}
                </div>
            )}

            {isQuickDepositOpenedFirstTime && (
                <div
                    data-testid="WidgetAccount_depositBlock"
                    className={clsx(style.quickDepositBlock, { [style.opened]: isQuickDepositOpened })}
                >
                    <Suspense fallback="">
                        <QuickDepositIframe />
                    </Suspense>
                </div>
            )}
        </div>
    );
};

